import type { Box } from './drop-feedback';

export interface DropView {
  /** Every place the block could go: the slot it belongs to. */
  group: Box;
  /** Where the block is now. */
  origin: Box;
  /** The space it will take. */
  ghost: Box;
  label: string;
  isUnchanged: boolean;
}

export interface DropLayer {
  show: (view: DropView) => void;
  hide: () => void;
}

/** Under the overlay's insert bar (999999) and drag preview, over page content. */
const Z_INDEX = '999997';

const BRAND = '85, 107, 252';
const NEUTRAL = '107, 114, 128';

const place = (element: HTMLElement, box: Box): void => {
  Object.assign(element.style, {
    top: `${box.top}px`,
    left: `${box.left}px`,
    width: `${box.width}px`,
    height: `${box.height}px`,
  });
};

const create = (role: string, style: Partial<CSSStyleDeclaration>): HTMLDivElement => {
  const element = document.createElement('div');
  element.dataset.enecoDrop = role;
  element.setAttribute('aria-hidden', 'true');
  Object.assign(element.style, { position: 'fixed', pointerEvents: 'none', boxSizing: 'border-box', ...style });
  return element;
};

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The drop indicator: fixed boxes in `document.body`, for the same reasons the Studio highlight is
 * (see `studio-highlight.ts`) — none of it touches the page's markup, layout or hit testing.
 *
 * Three boxes, each answering one question: the slot tint says where blocks can go, the wash on the
 * origin says what is being moved, and the ghost says where it lands and how much room it takes.
 * The ghost glides between positions instead of jumping, so a fast drag across five blocks reads as one
 * movement.
 */
export const createDropLayer = (): DropLayer => {
  let boxes: { group: HTMLElement; origin: HTMLElement; ghost: HTMLElement; label: HTMLElement } | undefined;

  const build = () => {
    const group = create('group', { zIndex: Z_INDEX, background: `rgba(${BRAND}, 0.05)` });
    const origin = create('origin', {
      zIndex: Z_INDEX,
      background: `rgba(${NEUTRAL}, 0.3)`,
      outline: `2px dashed rgba(${NEUTRAL}, 0.7)`,
      outlineOffset: '-2px',
    });
    const ghost = create('ghost', {
      zIndex: Z_INDEX,
      borderRadius: '4px',
      transition: prefersReducedMotion()
        ? 'none'
        : 'top 90ms ease-out, left 90ms ease-out, width 90ms ease-out, height 90ms ease-out',
    });
    const label = document.createElement('span');
    Object.assign(label.style, {
      position: 'absolute',
      top: '8px',
      left: '8px',
      padding: '4px 8px',
      borderRadius: '999px',
      color: '#fff',
      font: '600 12px/1 system-ui, sans-serif',
      whiteSpace: 'nowrap',
    });
    ghost.append(label);
    document.body.append(group, origin, ghost);
    return { group, origin, ghost, label };
  };

  const hide = (): void => {
    if (!boxes) return;
    boxes.group.remove();
    boxes.origin.remove();
    boxes.ghost.remove();
    boxes = undefined;
  };

  const show = ({ group, origin, ghost, label, isUnchanged }: DropView): void => {
    boxes ??= build();
    place(boxes.group, group);
    place(boxes.origin, origin);
    place(boxes.ghost, ghost);

    const colour = isUnchanged ? NEUTRAL : BRAND;
    Object.assign(boxes.ghost.style, {
      border: `2px dashed rgb(${colour})`,
      background: `rgba(${colour}, ${isUnchanged ? 0.08 : 0.14})`,
    });
    boxes.label.style.background = `rgb(${colour})`;
    boxes.label.textContent = label;
  };

  return { show, hide };
};
